import Head from "next/head"
import * as Sentry from "@sentry/node"
import { AppProps } from "next/app"
import { useRouter } from "next/router"
import {
  analytics,
  API,
  auth,
  DEV_ENVIRONMENTS,
  LanguageOption,
} from "@project/shared"
import { useEffect, useState } from "react"
import { signOut, onAuthStateChanged } from "firebase/auth"
import { message, Spin } from "antd"
import { CloseCircleFilled } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { QueryClient, QueryClientProvider } from "react-query"
import { GlobalStyles, AuthProvider } from "../utils"
import "../utils/css-imports"
import { logEvent, setCurrentScreen } from "firebase/analytics"
import styled from "styled-components"

const queryClient = new QueryClient({ defaultOptions: {} })

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: process.env.NODE_ENV !== "development",
    environment: `owner-${process.env.NODE_ENV}`,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}
const LoaderWrapper = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`
const MyApp = ({ Component, pageProps }: AppProps) => {
  const routers = useRouter()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [isChild, setIsChild] = useState(false)
  const [facilities, setFacilities] = useState(null)
  const [childInformation, setChildInformation] = useState(null)
  const [isInfoLoading, setIsInfoLoading] = useState(true)
  const { t } = useTranslation()

  const logOut = () => {
    signOut(auth)
    setUser(null)
    window.location.href = "/login"
  }

  const initialLoad = () => {
    onAuthStateChanged(auth, async (user) => {
      try {
        if (user !== null) {
          const idToken = await user!.getIdTokenResult()
          if (idToken.claims["company_child"]) {
            setUser(user)
            setIsChild(true)
            const facilitiesResponse = await API.get("/child-parent/facilities")
            setFacilities(facilitiesResponse.data)
            const childInfo = await API.get("/child-parent/me")
            // Log out child if no active contract facilities found
            if (!childInfo?.data?.active_contract_facility) {
              message.error({
                key: "2",
                icon: (
                  <CloseCircleFilled onClick={() => message.destroy("2")} />
                ),
                content: t(
                  "You cannot login at this moment. Please contact your administrator."
                ),
              })

              signOut(auth)
              setUser(null)
            }
            setChildInformation(childInfo?.data)
            setIsInfoLoading(false)
            if (
              childInfo &&
              !childInfo?.data?.my_page_setting?.activity_record &&
              routers.pathname === "/"
            ) {
              routers.push("/service-provision-record")
            }
          } else {
            signOut(auth)
            setUser(null)

            message.error({
              key: "01",
              icon: <CloseCircleFilled onClick={() => message.destroy("01")} />,
              content: t("Unauthorized user"),
            })
          }
        }
        setLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        message.error({
          key: "02",
          content: t("Something went wrong. Please contact administrator."),
          icon: <CloseCircleFilled onClick={() => message.destroy("02")} />,
        })
        setIsInfoLoading(false)
      }
    })
  }
  useEffect(() => {
    initialLoad()
    if (process.env.NODE_ENV === "production") {
      const logAnalyticsEvent = (url: string) => {
        setCurrentScreen(analytics, url)
        logEvent(analytics, "screen_view", {
          firebase_screen: url,
          firebase_screen_class: "skeleton-owner",
        })
      }
      routers.events.on("routeChangeComplete", (url) => {
        logAnalyticsEvent(url)
      })

      logAnalyticsEvent(window.location.pathname)
      return () => {
        routers.events.off("routeChangeComplete", logAnalyticsEvent)
      }
    }
  }, [])

  useEffect(() => {
    if (user && childInformation && routers.pathname === "/") {
      if (childInformation?.my_page_setting?.service_provision_record) {
        routers.push("/service-provision-record")
      } else if (childInformation?.my_page_setting?.contact_facility) {
        routers.push("/contact-facility")
      } else if (childInformation?.my_page_setting?.activity_record) {
        routers.push("/program-record")
      } else if (
        childInformation?.my_page_setting?.service_provision_record_sheet
      ) {
        routers.push("/service-provision-record-sheet")
      } else if (childInformation?.my_page_setting?.mail_setting) {
        routers.push("/change-email")
      } else if (
        childInformation?.my_page_setting?.individual_support_plan_list
      ) {
        routers.push("/individual-support-plans")
      } else if (
        childInformation?.my_page_setting?.individual_support_plan_draft_list
      ) {
        routers.push("/individual-support-draft-plans")
      } else if (childInformation?.my_page_setting?.monitoring_list) {
        routers.push("/monitoring")
      }
    }
  }, [childInformation, user])

  if (routers.pathname !== "/login" && user && (loading || isInfoLoading)) {
    return (
      <LoaderWrapper>
        <Spin size={"large"} className={"loader"} />
      </LoaderWrapper>
    )
  }

  return (
    <>
      <Head>
        <title>{t("User management screen")}</title>
        <meta
          name={"viewport"}
          content={
            "width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
          }
        />
        <link rel={"preconnect"} href={"https://fonts.gstatic.com"} />
        <link
          href={
            "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap"
          }
          rel={"stylesheet"}
        />
      </Head>
      <GlobalStyles />
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          loading={loading}
          user={user}
          isOwner={isChild}
          setUser={setUser}
          logOut={logOut}
          facilities={facilities}
          childInformation={childInformation}
          isInfoLoading={isInfoLoading}
        >
          <Component {...pageProps} />

          {DEV_ENVIRONMENTS.includes(process.env.NEXT_PUBLIC_ENVIRONMENT) ? (
            <LanguageOption />
          ) : (
            <></>
          )}
        </AuthProvider>
      </QueryClientProvider>
    </>
  )
}

export default MyApp
